import {
  EntityInfo,
  EntityInfoPathHandler,
  EntityInfoUtils
} from '@record-it-npm/synchro-common';
import { DisposableContainer } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { ObjectUtils } from 'record-it-utils/utils/ObjectUtils/ObjectUtils';
import {
  RepositoryByEntityName,
  EntityRepositoryContainer
} from '../EntityRepositoryContainer/EntityRepositoryContainer';
import {
  EntityIdUpgradedPayload,
  EntitySynchronization
} from '../EntitySynchronization/EntitySynchronization';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';

export class EntityLocalIdUpdater<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>,
  TRepositoryByEntityName extends RepositoryByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  >
> {
  private referenceFieldsByEntityName: ReferenceFieldsByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  > = {};
  private disposableContainer: DisposableContainer = new DisposableContainer();

  constructor(
    private readonly entitySynchronization: EntitySynchronization<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >,
    private readonly entityRepositoryContainer: EntityRepositoryContainer<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName,
      TRepositoryByEntityName
    >
  ) {}

  public async init(): Promise<void> {
    this.referenceFieldsByEntityName = this.createReferenceFieldsByEntityName();

    this.disposableContainer.add(
      this.entitySynchronization.registerHooks({
        entityIdUpgraded: this.handleEntityIdUpgraded.bind(this)
      })
    );
  }

  public destroy(): void {
    this.referenceFieldsByEntityName = {};
    this.disposableContainer.disposeAll();
  }

  private handleEntityIdUpgraded(
    payload: EntityIdUpgradedPayload<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ): void {
    const referenceFieldInfos =
      this.referenceFieldsByEntityName[payload.entityName] ?? [];
    const originalIds = new Set(payload.entity.originalIds);

    for (const referenceFieldInfo of referenceFieldInfos) {
      this.patchReferenceFieldInRepository(
        payload.entity.id,
        originalIds,
        referenceFieldInfo
      );
      this.patchQueueItemReferenceField(
        payload.entity.id,
        originalIds,
        referenceFieldInfo
      );
    }
  }

  private patchReferenceFieldInRepository(
    newId: EntityManagerBaseEntity['id'],
    originalIds: Set<string>,
    info: ReferenceFieldInfo<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ): void {
    const repository = this.entityRepositoryContainer.getByEntityName(
      info.entityName
    );
    const entities = repository.getAllIncludingInvisibleEntities();

    for (const entity of entities) {
      const hasReference = info.pathHandler.some({
        data: entity,
        callback: ({ value }) => value != null && originalIds.has(value)
      });

      if (hasReference) {
        const copy = ObjectUtils.copyDeep(entity);

        info.pathHandler.forEachValue({
          data: copy,
          callback: ({ fieldInfo, data, value }) => {
            if (value != null && originalIds.has(value)) {
              data[fieldInfo.name] = newId;
            }
          }
        });

        repository.update(copy);
      }
    }
  }

  private patchQueueItemReferenceField(
    newId: EntityManagerBaseEntity['id'],
    originalIds: Set<string>,
    info: ReferenceFieldInfo<
      TSynchronizationEnvironmentTypes,
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
      TEntityTypesByEntityName
    >
  ): void {
    this.entitySynchronization.modifyQueueItems((queueItemInfo) => {
      let modified = false;

      if (queueItemInfo.entityName === info.entityName) {
        info.pathHandler.forEachValue({
          data: queueItemInfo.entity,
          callback: ({ fieldInfo, data }) => {
            if (data[fieldInfo.name] && originalIds.has(data[fieldInfo.name])) {
              data[fieldInfo.name] = newId;
              modified = true;
            }
          }
        });
      }

      return modified;
    });
  }

  private createReferenceFieldsByEntityName(): ReferenceFieldsByEntityName<
    TSynchronizationEnvironmentTypes,
    TEntityTypesByEntityName
  > {
    const referenceFieldsByEntityName: ReferenceFieldsByEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    > = {};
    const repositories = this.entityRepositoryContainer.getAll();

    for (const repository of repositories) {
      const entityInfo = repository.getEntityInfo() as EntityInfo<
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
        TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
        TEntityTypesByEntityName[TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]['entity']
      >;

      this.addFieldInfosToReferenceFieldsByEntityName(
        referenceFieldsByEntityName,
        entityInfo
      );
    }

    return referenceFieldsByEntityName;
  }

  /**
   * @param referenceFieldsByEntityName - gets modified in place
   * @param entityRefFieldInfos
   */
  private addFieldInfosToReferenceFieldsByEntityName<
    TEntityName extends
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']
  >(
    referenceFieldsByEntityName: ReferenceFieldsByEntityName<
      TSynchronizationEnvironmentTypes,
      TEntityTypesByEntityName
    >,
    entityInfo: EntityInfo<
      TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
      TEntityName,
      TEntityTypesByEntityName[TEntityName]['entity']
    >
  ): void {
    const refFieldInfos = EntityInfoUtils.getAllRefFieldInfos(entityInfo);

    for (const fieldInfo of refFieldInfos) {
      let infos = referenceFieldsByEntityName[fieldInfo.ref];
      if (!infos) {
        infos = referenceFieldsByEntityName[fieldInfo.ref] = [];
      }

      infos.push({
        entityName: entityInfo.entityName,
        pathHandler: new EntityInfoPathHandler({
          entityInfo,
          path: fieldInfo.path
        })
      });
    }
  }
}

type ReferenceFieldsByEntityName<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = Partial<{
  [key in TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']]: Array<
    ReferenceFieldInfo<
      TSynchronizationEnvironmentTypes,
      key,
      TEntityTypesByEntityName
    >
  >;
}>;

type ReferenceFieldInfo<
  TSynchronizationEnvironmentTypes extends
    AnySynchronizationEnvironmentTypesConstraint,
  TEntityName extends
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'],
  TEntityTypesByEntityName extends
    EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>
> = {
  entityName: TEntityName;
  pathHandler: EntityInfoPathHandler<
    TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'],
    TEntityTypesByEntityName[TEntityName]['entity']
  >;
};
